import React, { useCallback, useEffect, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiPlus, FiRefreshCw, FiSearch } from 'react-icons/fi';
import { CustomersService, LocationsService } from '../services/ZashaniService';
import { ButtonSpinner, PanelLoader, TableEmptyState } from '../components/LoadingStates';

const initialForm = { fullName: '', phone: '', address: '', location: { department: '', municipality: '', zone: '' }, notes: '' };
const zoneOptions = Array.from({ length: 25 }, (_, index) => `Zona ${index + 1}`);
const CUSTOMER_PAGE_SIZE = 25;

const Customers = () => {
    const [customers, setCustomers] = useState([]);
    const [customerPage, setCustomerPage] = useState({ total: 0, page: 1, pages: 1, limit: CUSTOMER_PAGE_SIZE });
    const [departments, setDepartments] = useState([]);
    const [form, setForm] = useState(initialForm);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    const load = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const [rows, locationRows] = await Promise.all([
                CustomersService.listPage({ search: search.trim() || undefined, page, limit: CUSTOMER_PAGE_SIZE, sort: 'name' }),
                LocationsService.departments(),
            ]);
            setCustomers(rows.items || []);
            setCustomerPage({
                total: rows.total || 0,
                page: rows.page || page,
                pages: rows.pages || 1,
                limit: rows.limit || CUSTOMER_PAGE_SIZE,
            });
            setDepartments(locationRows);
        } catch (error) {
            setMessage(error.response?.data?.message || 'No fue posible cargar clientes.');
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            load(1);
        }, 280);
        return () => clearTimeout(timeout);
    }, [load]);

    const submit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setMessage('');
        try {
            await CustomersService.create(form);
            setForm(initialForm);
            setMessage('Cliente guardado.');
            await load(1);
        } catch (error) {
            setMessage(error.response?.data?.message || 'No fue posible guardar el cliente.');
        } finally {
            setSaving(false);
        }
    };

    const municipalities = departments.find((department) => department.name === form.location.department)?.municipalities || [];

    return (
        <section className="two-column">
            <form className={`panel ${saving ? 'is-busy' : ''}`} onSubmit={submit}>
                {saving && <PanelLoader label="Guardando cliente..." />}
                <h2>Nuevo cliente</h2>
                {message && <div className="notice">{message}</div>}
                <label>Nombre completo<input value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} required /></label>
                <label>Telefono<input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required /></label>
                <div className="form-grid three">
                    <label>Departamento
                        <select value={form.location.department} onChange={(e) => setForm({ ...form, location: { department: e.target.value, municipality: '', zone: '' } })} required>
                            <option value="">Seleccionar departamento</option>
                            {departments.map((department) => <option key={department.code} value={department.name}>{department.name}</option>)}
                        </select>
                    </label>
                    <label>Municipio
                        <select value={form.location.municipality} onChange={(e) => setForm({ ...form, location: { ...form.location, municipality: e.target.value, zone: '' } })} disabled={!form.location.department} required>
                            <option value="">Seleccionar municipio</option>
                            {municipalities.map((municipality) => <option key={municipality.code} value={municipality.name}>{municipality.name}</option>)}
                        </select>
                    </label>
                    <label>Zona
                        <select value={form.location.zone} onChange={(e) => setForm({ ...form, location: { ...form.location, zone: e.target.value } })} disabled={!form.location.municipality}>
                            <option value="">Sin zona</option>
                            {zoneOptions.map((zone) => <option key={zone} value={zone}>{zone}</option>)}
                        </select>
                    </label>
                </div>
                <label>Direccion<textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required /></label>
                <label>Notas<textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
                <button className="primary-button" type="submit" disabled={saving}>
                    {saving ? <ButtonSpinner /> : <FiPlus />}
                    <span>{saving ? 'Guardando...' : 'Guardar'}</span>
                </button>
            </form>
            <div className="panel">
                <div className="table-toolbar">
                    <div>
                        <h2>Clientes</h2>
                        <span className="table-subtitle">{customerPage.total} cliente{customerPage.total === 1 ? '' : 's'} en orden alfabetico</span>
                    </div>
                    <button className={`icon-button ${loading ? 'is-spinning' : ''}`} type="button" onClick={() => load(customerPage.page)} disabled={loading} aria-label="Actualizar clientes"><FiRefreshCw /></button>
                </div>
                <div className="customer-list-controls">
                    <label className="search-field">Buscar cliente
                        <FiSearch />
                        <input placeholder="Nombre, telefono o ubicacion" value={search} onChange={(e) => setSearch(e.target.value)} />
                    </label>
                </div>
                <div className="table-wrap">
                    {loading || customers.length === 0 ? (
                        <TableEmptyState loading={loading} emptyText="No hay clientes para mostrar." />
                    ) : (
                        <table>
                            <thead><tr><th>Nombre</th><th>Telefono</th><th>Ubicacion</th><th>Direccion</th></tr></thead>
                            <tbody>
                                {customers.map((customer) => (
                                    <tr key={customer._id}>
                                        <td>{customer.fullName}</td>
                                        <td>{customer.phone}</td>
                                        <td>{customer.location?.department || 'Sin departamento'}<small>{customer.location?.municipality || 'Sin municipio'} {customer.location?.zone || ''}</small></td>
                                        <td>{customer.address}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <div className="pagination-bar">
                    <button className="ghost-button" type="button" disabled={loading || customerPage.page <= 1} onClick={() => load(customerPage.page - 1)}>
                        <FiChevronLeft />
                        <span>Anterior</span>
                    </button>
                    <span>Pagina {customerPage.page} de {customerPage.pages}</span>
                    <button className="ghost-button" type="button" disabled={loading || customerPage.page >= customerPage.pages} onClick={() => load(customerPage.page + 1)}>
                        <span>Siguiente</span>
                        <FiChevronRight />
                    </button>
                </div>
            </div>
        </section>
    );
};

export default Customers;
